import { token } from '@atlas-frontend/design-tokens';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { Button, Descriptions, Divider, Flex } from 'antd';
import { FC, useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  Bleed,
  CodeBlock,
  Main,
  ObjectHeader,
  Padding,
  SidePanel,
} from '../../common/components';
import { TextSecondary } from '../../common/components/typography';
import { getDatePeriodString } from '../../common/utils';
import { useToggleKeys } from '../../common/utils/useToggleKeys';
import { DataObjectDescriptionVersionStore } from '../DataObjectDescriptionVersionStore';
import { RequiredKeyDataType } from '../schema';
import { useDataObjectFormLabelTranslation } from '../useDataObjectFormLabelTranslation';
import { usePanelParams } from '../usePanelParams';
import { AlgorithmAlternativeCell } from './AlgorithmAlternativeCell';
import { AlgorithmStatus } from './AlgorithmStatus';
import { AlgorithmTargetsView } from './AlgorithmTargetsView';
import translations from './AlgorithmViewPanel.translations.json';
import { AlgorithmFilterClause } from './filterClause/AlgorithmFilterClause';
import { AlgorithmJoins } from './joins/AlgorithmJoins';
import { useAlgorithmJoinsDataQuery } from './joins/useAlgorithmJoinsDataQuery';
import { AlgorithmJoinsWithMappingsGraphFormModal } from './joinsAndMappings/graph/AlgorithmJoinsWithMappingsGraphFormModal';
import { AlgorithmMappings } from './mappings/AlgorithmMappings';
import { OpenCloseAllBlocksButtons } from './OpenCloseAllBlocksButtons';
import { useGetAlgorithmQuery } from './query/useGetAlgorithmQuery';
import { AlgorithmType } from './schema/zAlgorithmType';
import { AlgorithmSources } from './sources/AlgorithmSources';
import { findAlgorithmAlternative, initAlgorithmAlternatives } from './utils';
import { TestCalculateAlgorithmsModal } from './testRun/AlgorithmTestCalculateModal';
import { DataObjectFormType, DataObjectType } from '../types';
import { useBrokenReferences } from './brokenReferences/useBrokenReferences';
import { useAlertContext } from '../../common/form';
import { BrokenReferencesAlertWithDrawer } from './brokenReferences/BrokenReferencesAlertWithDrawer';
import { CheckBrokenReferencesButton } from './brokenReferences/CheckBrokenReferencesButton';
import { AlgorithmSequenceNumber } from './AlgorithmSequenceNumber';

const { addTranslations, useTranslation } = i18nNsFactory('algorithms-view');
addTranslations(translations);

const blockKeys = ['sources', 'mappings', 'joins', 'filterClause', 'targets'];

export interface AlgorithmsViewPanelProps {
  algorithmId: number;
  requiredKeys?: RequiredKeyDataType[];
}

export const AlgorithmViewPanel: FC<AlgorithmsViewPanelProps> = (props) => {
  const { algorithmId, requiredKeys } = props;
  const { t } = useTranslation();
  const { t: tLabel } = useDataObjectFormLabelTranslation();
  const { data: algorithm, isFetching } = useGetAlgorithmQuery({
    id: algorithmId,
  });
  const [params, setPanelParams] = usePanelParams();
  const targetId = params.targetId ? Number(params.targetId) : undefined;

  const { resetAlert, setSuccess } = useAlertContext();

  const isDbScriptEnabled = algorithm?.expression !== '';
  const [dataObjectDescriptionVersion] =
    DataObjectDescriptionVersionStore.useValue('dataObjectDescriptionVersion');
  const isInput =
    dataObjectDescriptionVersion.formType === DataObjectFormType.IN;

  const [testRunModalOpen, setTestRunModalOpen] = useState(false);

  const {
    brokenReferencesMap,
    isFetching: isChecking,
    refetch,
  } = useBrokenReferences([algorithm?.id ?? 0]);

  const handleCheckBrokenReferences = useCallback(async () => {
    resetAlert();
    const { data } = await refetch();
    if (data?.errors.length === 0) {
      setSuccess(t('alert.no-broken-references'));
    }
  }, [resetAlert, refetch, setSuccess, t]);

  const alternative = useMemo(
    () =>
      findAlgorithmAlternative(
        algorithm?.code || '',
        algorithm?.groupCode || '',
        initAlgorithmAlternatives(
          dataObjectDescriptionVersion.alternativeAlgorithmSets ?? [],
        ),
      ),
    [dataObjectDescriptionVersion, algorithm],
  );

  const {
    toggle: toggleBlock,
    isEnabled: isBlockExpanded,
    clear: closeAllBlocks,
    fill: openBlocks,
  } = useToggleKeys(blockKeys);

  const [
    isJoinsAndMappingsGraphModalOpen,
    setIsJoinsAndMappingsGraphModalOpen,
  ] = useState(false);

  const {
    data: algorithmJoinsAndMappingsData,
    isFetching: isAlgorithmJoinsAndMappingsDataFetching,
  } = useAlgorithmJoinsDataQuery(algorithmId);

  const descriptionItems = useMemo(() => {
    const items = [];
    if (algorithm) {
      items.push({
        key: 'typeCode',
        label: tLabel('algorithms.type.label'),
        children: tLabel('algorithms.type.' + algorithm.typeCode),
      });
      items.push({
        key: 'validPeriod',
        label: tLabel('algorithms.validPeriod.label'),
        children: getDatePeriodString(
          algorithm.validPeriod[0],
          algorithm.validPeriod[1],
        ),
      });
      if (algorithm.typeCode === AlgorithmType.INSERT) {
        items.push({
          key: 'groupCode',
          label: tLabel('algorithms.groupCode.label'),
          children: algorithm.groupCode,
        });
        if (
          dataObjectDescriptionVersion?.dataObjectType === DataObjectType.OUT
        ) {
          items.push({
            key: 'alternative',
            label: tLabel('algorithms.alternative.label'),
            children: <AlgorithmAlternativeCell alternativeSet={alternative} />,
          });
        }
      }
      if (dataObjectDescriptionVersion?.dataObjectType === DataObjectType.OUT) {
        items.push({
          key: 'expression',
          label: tLabel('algorithms.expression.label'),
          children: t(`expression.${Boolean(algorithm.expression)}`),
        });
      }
      if (
        !Boolean(algorithm.expression) &&
        !isInput &&
        algorithm.typeCode === AlgorithmType.INSERT
      ) {
        items.push({
          key: 'aggregatePeriods',
          label: tLabel('algorithms.aggregatePeriods.label'),
          children: t(
            `aggregatePeriods.${Boolean(algorithm.aggregatePeriods)}`,
          ),
        });
      }
      items.push({
        key: 'sequenceNumber',
        label: tLabel('algorithms.sequenceNumber.label'),
        children: (
          <AlgorithmSequenceNumber
            sequenceNumber={algorithm.sequenceNumber}
            execAfterAggregation={algorithm.execAfterAggregation}
          />
        ),
      });
    }
    return items;
  }, [
    algorithm,
    alternative,
    dataObjectDescriptionVersion?.dataObjectType,
    isInput,
    t,
    tLabel,
  ]);

  if (!algorithm || isFetching) {
    return null;
  }

  const hasDbScriptEnabled = Boolean(algorithm.expression);

  return (
    <SidePanel
      side="right"
      width={1786}
      minWidth={700}
      collapsedWidth={700}
      mode="layered"
    >
      <SidePanelWrapper>
        <BrokenReferencesAlertWithDrawer
          brokenReferencesMap={brokenReferencesMap}
          tLabel={tLabel}
        />
        <ScrollContent>
          <Flex vertical gap="large">
            <Flex align="end" justify="space-between">
              <ObjectHeader
                upperText={algorithm.code}
                lowerText={t('heading')}
              />
              <AlgorithmStatus statusId={algorithm.statusId} />
            </Flex>
            <Bleed direction="horizontal">
              <Divider />
            </Bleed>
            <Flex vertical gap="small">
              <Descriptions column={2} items={descriptionItems} />
              <Descriptions
                column={1}
                items={[
                  {
                    key: 'description',
                    label: tLabel('algorithms.description.label'),
                    children: algorithm.description,
                  },
                ]}
              />
            </Flex>
            {!hasDbScriptEnabled && (
              <Flex gap="middle" justify="end">
                <OpenCloseAllBlocksButtons
                  onClickOpen={openBlocks}
                  onClickClose={closeAllBlocks}
                />
              </Flex>
            )}
            <AlgorithmSources
              algorithmId={algorithm.id}
              expanded={isBlockExpanded('sources')}
              onToggle={() => toggleBlock('sources')}
            />
            {!isDbScriptEnabled && (
              <>
                <AlgorithmMappings
                  algorithmId={algorithm.id}
                  expanded={isBlockExpanded('mappings')}
                  onToggle={() => toggleBlock('mappings')}
                  mappings={algorithmJoinsAndMappingsData?.mappings}
                  isLoading={isAlgorithmJoinsAndMappingsDataFetching}
                  onEditGraphClick={() =>
                    setIsJoinsAndMappingsGraphModalOpen(true)
                  }
                  editGraphLabel={t('view-graph')}
                />
                {algorithm.typeCode === AlgorithmType.INSERT && (
                  <>
                    <AlgorithmJoins
                      algorithmId={algorithm.id}
                      expanded={isBlockExpanded('joins')}
                      onToggle={() => toggleBlock('joins')}
                      joins={algorithmJoinsAndMappingsData?.joins}
                      sources={algorithmJoinsAndMappingsData?.sources}
                      isLoading={isAlgorithmJoinsAndMappingsDataFetching}
                      onEditGraphClick={() =>
                        setIsJoinsAndMappingsGraphModalOpen(true)
                      }
                      editGraphLabel={t('view-graph')}
                    />
                    {isJoinsAndMappingsGraphModalOpen &&
                      algorithmJoinsAndMappingsData && (
                        <AlgorithmJoinsWithMappingsGraphFormModal
                          readOnly
                          mappings={algorithmJoinsAndMappingsData.mappings}
                          joins={algorithmJoinsAndMappingsData.joins}
                          sources={algorithmJoinsAndMappingsData.sources}
                          sourceDimensionsMap={
                            algorithmJoinsAndMappingsData.sourceDimensionsMap
                          }
                          onClose={() =>
                            setIsJoinsAndMappingsGraphModalOpen(false)
                          }
                        />
                      )}
                  </>
                )}
                <AlgorithmFilterClause
                  algorithmId={algorithm.id}
                  expanded={isBlockExpanded('filterClause')}
                  onToggle={() => toggleBlock('filterClause')}
                />
                <AlgorithmTargetsView
                  algorithmId={algorithm.id}
                  algorithmTypeCode={algorithm.typeCode}
                  requiredKeys={requiredKeys}
                  targetId={targetId}
                  expanded={
                    targetId !== undefined || isBlockExpanded('targets')
                  }
                  onToggle={() => toggleBlock('targets')}
                  editGraphLabel={t('view-graph')}
                />
              </>
            )}
            {hasDbScriptEnabled && (
              <>
                <Bleed direction="horizontal">
                  <Divider />
                </Bleed>
                <Flex vertical gap="small">
                  <TextSecondary>
                    {tLabel('algorithms.expression.label')}
                  </TextSecondary>
                  <CodeBlock>{algorithm.expression}</CodeBlock>
                </Flex>
              </>
            )}
          </Flex>
        </ScrollContent>
        <Main.Footer>
          <FullWidthFlex justify="end" gap="small">
            {dataObjectDescriptionVersion?.dataObjectType ===
              DataObjectType.OUT && (
              <Button
                id="algorithmForm-testRun"
                onClick={() => setTestRunModalOpen(true)}
              >
                {t('button.testRun')}
              </Button>
            )}

            <CheckBrokenReferencesButton
              onClick={handleCheckBrokenReferences}
              loading={isFetching || isChecking}
            />
            <Button
              id="algorithViewPanel-close"
              onClick={() => setPanelParams()}
            >
              {t('button.close')}
            </Button>
          </FullWidthFlex>
        </Main.Footer>

        {testRunModalOpen && (
          <TestCalculateAlgorithmsModal
            algorithms={[algorithm]}
            onClose={() => setTestRunModalOpen(false)}
          />
        )}
      </SidePanelWrapper>
    </SidePanel>
  );
};
const FullWidthFlex = styled(Flex)`
  width: 100%;
`;

const SidePanelWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
  border-left: 1px solid ${token.colorBorderSecondary};
`;

const ScrollContent = styled(Padding)`
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1;
`;
